import { AudioLibraryUsage } from '@weave/schema-gen-ts/dist/schemas/phone/audio-library/v1/audio_library_service.pb';
import { useQuery } from 'react-query';
import { AudioLibraryApi, AudioLibraryTypes } from '@frontend/api-audio-library';
import { Trans, useTranslation } from '@frontend/i18n';
import { ModalControlModalProps, Text, useAlert } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { logError } from '../../utils/loggers';
import { DeleteFlow } from '../common/delete-flow';
import { useAudioLibraryMutations } from './use-audio-library-mutations';

export const AudioFileDeleteModal = ({
  modalProps,
  name,
  id,
  phoneTenantId,
  disableLinks = false,
}: {
  modalProps: ModalControlModalProps;
  name: string;
  id: string;
  phoneTenantId?: string;
  disableLinks?: boolean;
}) => {
  const {
    data = { allUsages: [] },
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: [phoneTenantId, ...queryKeys.settings.audioLibraryUsageV2(id)],
    queryFn: () =>
      AudioLibraryApi.getAudioLibraryUsageV2({
        mediaItemId: id,
        tenantId: phoneTenantId ?? '',
      }),
    enabled: modalProps.show,
    staleTime: 0,
    meta: {
      error: {
        message: 'AudioFileDeleteModal:audioLibraryUsageQuery | Failed to fetch audio library usage',
        data: { mediaItemId: id, phoneTenantId, name },
      },
    },
    select: (respData) => {
      // Filter out primitive usages that have primitive type of "IVR_MENU" since those don't need to block deletion
      // of the audio file. This is because IVR_MENU primitives means they are for phone trees that are not part of
      // a call route. If a phone tree is part of a call route, then it will be included in the call route usages.
      const filteredUsages =
        respData?.allUsages?.filter((usage) => {
          if ('primitiveUsage' in usage) {
            return usage.primitiveUsage.primitiveType !== AudioLibraryTypes.PrimitiveUsageType.IVR_MENU;
          }
          return true;
        }) ?? [];
      return { ...respData, allUsages: filteredUsages };
    },
  });

  return (
    <DeleteFlow.Modal
      CanDeleteContent={AudioLibraryCanDeleteContent}
      CannotDeleteContent={AudioLibraryCannotDeleteContent}
      id={id}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      modalProps={modalProps}
      name={name}
      phoneTenantId={phoneTenantId ?? ''}
      usage={data.allUsages || []}
      disableLinks={disableLinks}
    />
  );
};

const AudioLibraryCanDeleteContent = ({
  modalProps,
  name,
  id,
  phoneTenantId,
}: {
  modalProps: ModalControlModalProps;
  name: string;
  id: string;
  phoneTenantId: string;
}) => {
  const { remove } = useAudioLibraryMutations();
  const { t } = useTranslation('phone');
  const alerts = useAlert();

  const removeFn = () =>
    remove.mutate(
      { mediaItemId: id, tenantId: phoneTenantId },
      {
        onSuccess: () => {
          alerts.success(t('Successfully deleted Audio File.'));
          modalProps.onClose();
        },
        onError: (error) => {
          alerts.error(t('Failed to delete Audio File.'));
          logError('AudioLibraryCanDeleteContent:remove | Failed to delete audio file', {
            data: { mediaItemId: id, phoneTenantId, name, error },
          });
        },
      }
    );

  return (
    <DeleteFlow.CanDeleteContent
      modalProps={modalProps}
      name={name}
      title={t('Delete Audio File?')}
      removeFn={removeFn}
      body={
        <Trans t={t} name={name}>
          <Text>Are you sure you want to delete {{ name }}? This action cannot be undone.</Text>
        </Trans>
      }
    />
  );
};

const AudioLibraryCannotDeleteContent = ({
  id,
  modalProps,
  name,
  usage,
  disableLinks = false,
}: {
  id: string;
  modalProps: ModalControlModalProps;
  name: string;
  usage: AudioLibraryUsage[];
  disableLinks?: boolean;
}) => {
  const { t } = useTranslation('phone');

  const usageCount = usage.length;

  return (
    <DeleteFlow.CannotDeleteContentV2
      id={id}
      showTable={true}
      modalProps={modalProps}
      disableLinks={disableLinks}
      usages={usage}
      title={t('Cannot Delete Audio File')}
      body={
        <Trans t={t} name={name}>
          <Text>
            <Text as='span' weight='semibold'>
              {name}
            </Text>{' '}
            {usageCount === 1
              ? t(
                  'can’t be deleted because it is currently in use. The file must be removed from the following place in order to be deleted.'
                )
              : t(
                  'can’t be deleted because it is currently in use. The file must be removed from the following places in order to be deleted.'
                )}
          </Text>
        </Trans>
      }
    />
  );
};
